import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Panel } from '@tanium/react-panel';
import { Stack } from '@tanium/react-stack';
import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { RibbonColumn, StackedRibbonList } from '@tanium/react-stacked-ribbon-list';
import { Link } from '@tanium/react-link';
import { EditIcon, TrashIcon, AddIcon } from '@tanium/react-graphics';
import { BastionTargetAccess } from 'gql/BastionTargetAccess';
import ConfirmationModal from 'components/pages/request/requestPartials/ConfirmationModal';
import Helper from 'utils/Helper';
import { TARGET_GROUP_ROUTE } from 'Route';
import InlineCopyToClipboard from 'components/common/copyToClipboard/inlineCopyToClipboard';
import BastionTargetAccessSelectors from 'components/pages/console/store/BastionTargetAccessSelectors';
import BastionTargetAccessSlice from 'components/pages/console/store/BastionTargetAccessSlice';
import BastionTargetAccessCRUDModal, { Operation } from './bastionTargetAccessCRUDModal';

type BastionTargetAccessProps = {
  targetOrGroupID: string;
  allowEditing: boolean;
};

const BastionTargetAccessList: React.VFC<BastionTargetAccessProps> = ({ targetOrGroupID, allowEditing }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const loading = useSelector(BastionTargetAccessSelectors.selectBastionTargetAccessLoading);
  const mutationSucceeded = useSelector(BastionTargetAccessSelectors.selectMutationSucceeded);
  const bastionAccess = useSelector(BastionTargetAccessSelectors.selectBastionTargetAccesses);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedBastion, setSelectedAwsTargetRole] = useState<BastionTargetAccess | undefined>(undefined);
  const columns: RibbonColumn<BastionTargetAccess>[] = useMemo(
    () => [
      {
        label: t('ACTIONS'),
        dataKey: 'id',
        grow: 0,
        width: 50,
        isSortDisabled: true,
        renderer: (data) => {
          return allowEditing && data.itemData.targetOrTargetGroupID === targetOrGroupID ? (
            <div>
              <TooltippedIconButton
                data-testid={`bastionTargetAccess-edit-${data.index}`}
                tooltipContent={t('TARGET_ACCESS_EDIT')}
                label={t('TARGET_ACCESS_EDIT')}
                height={16}
                width={16}
                icon={EditIcon}
                onClick={() => {
                  setSelectedAwsTargetRole(data.itemData);
                  setIsOpen(true);
                }}
              />
              <TooltippedIconButton
                data-testid={`bastionTargetAccess-delete-${data.index}`}
                style={{ marginLeft: '0.5em' }}
                tooltipContent={t('TARGET_ACCESS_DELETE')}
                label={t('TARGET_ACCESS_DELETE')}
                height={16}
                width={16}
                icon={TrashIcon}
                onClick={() => {
                  setSelectedAwsTargetRole(data.itemData);
                  setIsDeleteConfirmationOpen(true);
                }}
              />
            </div>
          ) : (
            <></>
          );
        },
      },
      {
        label: t('BASTION_TARGET_ACCESS_NAME_LABEL'),
        dataKey: 'name',
        primary: true,
      },
      {
        label: t('ID'),
        dataKey: 'bastion-target-access-id',
        renderer: ({ itemData }) => {
          return (
            <div>
              <InlineCopyToClipboard
                displayedText={itemData.id}
                copyText={itemData.id}
                iconSize={{ width: 16, height: 16 }}
                tooltipContent={t('COPY_ID')}
              />
            </div>
          );
        },
      },
      {
        label: t('DESCRIPTION'),
        dataKey: 'description',
      },
      {
        label: t('AD_GROUPS'),
        dataKey: 'adGroups',
        renderer: ({ itemData }) => {
          return itemData.adGroups.join(', ');
        },
      },
      {
        label: t('BASTION_TARGET_ACCESS_LAUNCH_TEMPLATE_LABEL'),
        dataKey: 'launchTemplateName',
      },
      {
        label: t('BASTION_TARGET_ACCESS_AUTOSCALING_GROUP_LABEL'),
        dataKey: 'autoscalingGroupName',
      },
      {
        label: t('BASTION_TYPE'),
        dataKey: 'bastionType',
        renderer: ({ itemData }) => {
          return t(`BASTION_TYPE_${itemData.bastionType}`);
        },
      },
      {
        label: t('OPERATING_SYSTEM'),
        dataKey: 'operatingSystem',
        renderer: ({ itemData }) => {
          return itemData.operatingSystem
            ? t(`OPERATING_SYSTEM_${itemData.operatingSystem}`)
            : t('OPERATING_SYSTEM_UNSPECIFIED');
        },
      },
      {
        label: t('BASTION_KMS_KEY_ARN'),
        dataKey: 'proxyKMSKeyARN',
      },
      {
        label: t('BASTION_TARGET_ACCESS_PORT_FORWARD_LABEL'),
        dataKey: 'canPortForward',
        renderer: ({ itemData }) => {
          return itemData.canPortForward ? 'true' : 'false';
        },
      },
      {
        label: t('PARENT_ID'),
        dataKey: 'targetOrTargetGroupID',
        renderer: ({ itemData }) => {
          return itemData.targetOrTargetGroupID === targetOrGroupID ? (
            <>{t('SELF')}</>
          ) : (
            <Link
              data-testid={`link-${itemData.id}`}
              href={Helper.interpolateRoute(TARGET_GROUP_ROUTE.url, { targetGroupId: itemData.targetOrTargetGroupID })}
            >
              {itemData.targetOrTargetGroupID}
            </Link>
          );
        },
      },
    ],
    [setIsDeleteConfirmationOpen, setSelectedAwsTargetRole, setIsOpen, targetOrGroupID, t, allowEditing]
  );

  const createBastionTargetAccess = useCallback(() => {
    setSelectedAwsTargetRole(undefined);
    setIsOpen(true);
  }, [setIsOpen]);

  const dataGetter = useCallback(
    (index: number): BastionTargetAccess => {
      return bastionAccess[index];
    },
    [bastionAccess]
  );

  const itemIdGetter = useCallback(
    (index: number) => {
      return dataGetter(index).id;
    },
    [dataGetter]
  );

  const isItemLoaded = useCallback(
    (index: number) => {
      return !!dataGetter(index);
    },
    [dataGetter]
  );

  const isIdEmphasized = useCallback(
    (id: string) => {
      const item = bastionAccess.find((role) => role.id === id);
      return item?.targetOrTargetGroupID === targetOrGroupID;
    },
    [bastionAccess, targetOrGroupID]
  );

  const handleDelete = useCallback(
    (proceed: boolean) => {
      if (proceed && selectedBastion !== undefined) {
        dispatch(BastionTargetAccessSlice.actions.deleteBastionTargetAccess(selectedBastion.id));
      }

      setIsDeleteConfirmationOpen(false);
      setSelectedAwsTargetRole(undefined);
    },
    [setIsDeleteConfirmationOpen, setSelectedAwsTargetRole, selectedBastion, dispatch]
  );

  const handleCreateOrUpdate = useCallback(
    (operation: Operation) => {
      setIsOpen(false);

      if (operation.type === 'cancel') {
        return;
      }

      switch (operation.type) {
        case 'create':
          dispatch(
            BastionTargetAccessSlice.actions.createBastionTargetAccess({
              adGroups: operation.adGroups,
              targetOrTargetGroupID: operation.targetOrTargetGroupId,
              description: operation.description,
              name: operation.name,
              launchTemplateName: operation.launchTemplateName,
              autoscalingGroupName: operation.autoscalingGroupName,
              bastionType: operation.bastionType,
              operatingSystem: operation.operatingSystem,
              proxyKMSKeyARN: operation.proxyKMSKeyARN,
              canPortForward: operation.canPortForward,
            })
          );
          break;
        case 'update':
          dispatch(
            BastionTargetAccessSlice.actions.updateBastionTargetAccess({
              id: operation.id,
              adGroups: operation.adGroups,
              targetOrTargetGroupID: operation.targetOrTargetGroupId,
              description: operation.description,
              name: operation.name,
              launchTemplateName: operation.launchTemplateName,
              autoscalingGroupName: operation.autoscalingGroupName,
              bastionType: operation.bastionType,
              operatingSystem: operation.operatingSystem,
              proxyKMSKeyARN: operation.proxyKMSKeyARN,
              canPortForward: operation.canPortForward,
            })
          );
          break;
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (mutationSucceeded) {
      dispatch(
        BastionTargetAccessSlice.actions.listBastionTargetAccessForTargetOrGroup({
          includeAllTargetGroups: true,
          targetOrTargetGroupID: targetOrGroupID,
        })
      );
    }
  }, [dispatch, targetOrGroupID, mutationSucceeded]);

  const listActions = allowEditing ? (
    <Stack>
      <Button data-testid="bastionTargetAccess-create" size="sm" onClick={createBastionTargetAccess}>
        <Box pr={1} display={'inline-block'}>
          <AddIcon height={'10px'} />
        </Box>
        {t('CREATE')}
      </Button>
    </Stack>
  ) : (
    <></>
  );

  return (
    <Panel title={t('BASTION_TARGET_ACCESS_TITLE')}>
      <Stack direction="column" itemSpacing={1} data-testid="bastionTargetAccess">
        <StackedRibbonList
          enableRowHighlight={false}
          columns={columns}
          isItemLoaded={isItemLoaded}
          height={bastionAccess.length > 0 ? '12em' : '7.4em'}
          dataGetter={dataGetter}
          itemCount={bastionAccess.length}
          itemIdGetter={itemIdGetter}
          listActions={listActions}
          isIdEmphasized={isIdEmphasized}
          isLoading={loading}
        />
        <BastionTargetAccessCRUDModal
          isOpen={isOpen}
          bastionToEdit={selectedBastion}
          onProcess={handleCreateOrUpdate}
          targetOrTargetGroupId={targetOrGroupID}
        />
        <ConfirmationModal
          timeoutSeconds={1}
          headerText={t('BASTION_TARGET_ACCESS_DELETE_HEADER')}
          confirmationText={t('DELETE')}
          closeModal={handleDelete}
          isOpen={isDeleteConfirmationOpen}
        >
          <>{t('BASTION_TARGET_ACCESS_DELETE_CONTENT')}</>
        </ConfirmationModal>
      </Stack>
    </Panel>
  );
};

export default BastionTargetAccessList;
